import Vue from 'vue';
import VueRouter from 'vue-router';
import store from './store';
Vue.use(VueRouter);

// @ts-ignore
import NProgress from 'nprogress';

import {
  UserDetail,
  RoleEnum,
} from '@/models';

import { loginService, userService } from '@/services';

// Layouts
const ClientLayout = () => import('@/layouts/ClientLayout.vue');

// Views
const PageNotFound = () => import('@/views/shared/PageNotFound.vue');
const PageUnauthorized = () => import('@/views/shared/PageUnauthorized.vue');

const LoginPage = () => import('@/views/LoginPage.vue');
const HomePage = () => import('@/views/HomePage.vue');

const UserRegisterInputPage = () => import('@/views/register/UserRegisterInputPage.vue');
const ResetFirstLoginPage = () => import('@/views/password/ResetFirstLoginPage.vue');
const TireRegisterPage = () => import('@/views/tire/TireRegisterPage.vue');
const TireContractDetailPage = () => import('@/views/tire/TireContractDetailPage.vue');
const SearchTireContractPage = () => import('@/views/search-contract/SearchTireContractPage.vue');
const SearchContractAndRequestDelivery = () => import('@/views/search-contract/SearchContractAndRequestDeliveryPage.vue');
const WarehouseDeliveryPage = () => import('@/views/warehouse/WarehouseDeliveryPage.vue');
const DeliveryCalendar = () => import('@/views/delivery/DeliveryCalendarPage.vue');
const CheckTireContract = () => import('@/views/check-tire-contract/CheckTireContractPage.vue');
const PickUpCalendarPage = () => import('@/views/pickup/PickupCalendarPage.vue');
const SearchContractAndPickup = () => import('@/views/pickup/SearchContractAndPickupPage.vue');

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior (to, from, savedPosition) {
    return savedPosition ? savedPosition : { x: 0, y: 0 };
  },
  routes: [
    {
      path: '/login',
      name: 'routes.login',
      component: LoginPage,
      meta: {},
    },
    {
      path: '/reset-first-password',
      name: 'router.reset_first_password',
      component: ResetFirstLoginPage,
      meta: {},
    },
    {
      path: '/',
      redirect: '/home',
      component: ClientLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'home',
          name: 'routes.home',
          component: HomePage,
          meta: { requiresAuth: true, adminAuth: true, warehouseAuth: true, agencyAuth: true, btjCompanyAuth: false },
        },
        {
          path: 'unauthorized',
          name: 'routes.unauthorized',
          component: PageUnauthorized,
          meta: { requiresAuth: true, adminAuth: true, userAuth: true, companyAuth: true, btjCompanyAuth: true },
        },
        // User register
        {
          path: 'user-register-input',
          name: 'routes.user_register.input',
          component: UserRegisterInputPage,
          meta: { requiresAuth: true, adminAuth: true, agencyAuth: true },
        },
        // Tire contract register
        {
          path: 'tire-register',
          name: 'routes.tire_register',
          component: TireRegisterPage,
          meta: { requiresAuth: true, adminAuth: true, agencyAuth: true, warehouseAuth: true, btjCompanyAuth: true },
        },
        // Search tire contract
        {
          path: 'tire-contract-search',
          name: 'routes.search_tire_contract',
          component: SearchTireContractPage,
          meta: { requiresAuth: true, agencyAuth: true, warehouseAuth: true, btjCompanyAuth: true },
        },
        // Tire contract detail
        {
          path: 'tire-contract-detail/:id',
          name: 'routes.tire_contract_detail',
          component: TireContractDetailPage,
          meta: { requiresAuth: true, agencyAuth: true, warehouseAuth: true, btjCompanyAuth: true },
        },
        // Search tire contract and request delivery
        {
          path: 'delivery-request',
          name: 'routes.delivery_request',
          component: SearchContractAndRequestDelivery,
          meta: { requiresAuth: true, agencyAuth: true },
          props: (route) => ({
            date: route.query.date,
            totalDelivery: route.query.total_delivery,
          }),
        },
        // Delivery
        {
          path: 'delivery-calendar',
          name: 'routes.delivery-calendar',
          component: DeliveryCalendar,
          meta: { requiresAuth: true, agencyAuth: true },
        },
        // Check tire storing
        {
          path: 'check-tire-contract',
          name: 'routes.check-tire-contract',
          component: CheckTireContract,
          meta: { requiresAuth: true, agencyAuth: true },
        },
        // Pickup
        {
          path: 'pick-up-calendar',
          name: 'routes.pick-up-calendar',
          component: PickUpCalendarPage,
          meta: { requiresAuth: true, agencyAuth: true },
        },
        {
          path: 'search-contract-pickup',
          name: 'routes.search-contract-pickup',
          component: SearchContractAndPickup,
          meta: { requiresAuth: true, agencyAuth: true },
          props: (route) => ({
            date: route.query.date,
            totalPickup: route.query.total_pickup,
          }),
        },
        // Warehouse
        {
          path: 'warehouse-delivery',
          name: 'routes.warehouse.delivery',
          component: WarehouseDeliveryPage,
          meta: { requiresAuth: true, adminAuth: true, warehouseAuth: true },
        },
      ],
    },
    {
      path: '*',
      name: 'routes.404',
      component: PageNotFound,
      meta: {},
    },
  ],
});
router.beforeResolve((to, from, next) => {
  // If this isn't an initial page load.
  if (to.name) {
    // Start the route progress bar.
    NProgress.start();
  }
  next();
});
router.afterEach((to, from) => {
  // Complete the animation of the route progress bar.
  NProgress.done();
});
router.beforeEach((to, from, next) => {
  if (!loginService.isAuthenticated()) {
    if (to.meta.requiresAuth) {
      next('login');
      return;
    } else {
      next();
      return;
    }
  }

  if (!to.meta.requiresAuth) {
    next();
    return;
  }

  userService.getUserDetail().then((res) => {
    const user = res.data.data;
    if (user && user.role) {
      // Save to store
      store.dispatch('setUser', user);
      checkRole(to, from, next, user.role);
    }
  });
});
function checkRole(to: any, from: any, next: any, role: string) {
  // Check user roles
  let isAdmin: boolean = false;
  let isUser: boolean = false;
  let isWarehouse: boolean = false;
  let isAgency: boolean = false;
  let isCompany: boolean = false;
  let isBTJCompany: boolean = false;
  if (role === RoleEnum.ROLE_ADMIN || role === RoleEnum.ROLE_COMPANY_ADMIN) {
    isAdmin = true;
  } else if (role === RoleEnum.ROLE_USER) {
    isUser = true;
  } else if (role === RoleEnum.ROLE_WAREHOUSE) {
    isWarehouse = true;
  } else if (role === RoleEnum.ROLE_AGENCY) {
    isAgency = true;
  } else if (role === RoleEnum.ROLE_COMPANY) {
    isCompany = true;
  } else if (role === RoleEnum.ROLE_BTJ_COMPANY) {
    isBTJCompany = true;
  }
  if (isAdmin) {
    if (to.meta.adminAuth !== undefined && to.meta.adminAuth) {
      next();
    } else {
      next('/unauthorized');
    }
  } else if (isUser) {
    if (to.meta.userAuth !== undefined && to.meta.userAuth) {
      next();
    } else {
      next('/unauthorized');
    }
  } else if (isWarehouse) {
    if (to.meta.warehouseAuth !== undefined && to.meta.warehouseAuth) {
      next();
    } else {
      next('/unauthorized');
    }
  } else if (isAgency) {
    if (to.meta.agencyAuth !== undefined && to.meta.agencyAuth) {
      next();
    } else {
      next('/unauthorized');
    }
  } else if (isCompany) {
    if (to.meta.companyAuth !== undefined && to.meta.companyAuth) {
      next();
    } else {
      next('/unauthorized');
    }
  } else if (isBTJCompany) {
    if (to.meta.btjCompanyAuth !== undefined && to.meta.btjCompanyAuth) {
      next();
    } else {
      next('/unauthorized');
    }
  } else {
    next();
  }
}
export default router;
